import { ComponentProps, useEffect, useMemo } from 'react';
import { RowData, flexRender, useReactTable } from '@tanstack/react-table';
import { Header, headerPosition, HeaderRow } from './header/Header';
import { Row, Data } from './row/Row';
import { Footer } from './footer/Footer';
import { Box, Group, Stack } from '@mantine/core';
import { IconReload, IconDownload } from '@tabler/icons-react';
import { Filter } from './filters/Filter';
import { LoadingOverlay } from 'components/loading-overlay/LoadingOverlay';
import {
    FilterAvailabilityFeature,
    FilterAvailabilityInstance,
    FilterAvailabilityOptions,
    FilterAvailabilityTableState,
} from './features/filterAvailability';
import { DebouncedSearch } from './filters/DebouncedSearch';

import tableStyles from './Table.module.css';
import { ActionIcon } from 'components/action-icon/ActionIcon';
import { Button } from 'components/button/Button';
import { ColumnMetaBase, Table as TableType } from './types';
import { joinClassNames } from 'utils/joinClassNames';
import { TablePlaceholder } from './components/TablePlaceholder';
import { FilterButtonProps } from 'components/filter-button/FilterButton';

export * from '@tanstack/react-table';

/* eslint-disable @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unused-vars */
declare module '@tanstack/react-table' {
    // merge our new feature's state with the existing table state
    interface TableState extends FilterAvailabilityTableState {}
    // merge our new feature's options with the existing table options
    interface TableOptionsResolved<TData extends RowData> extends FilterAvailabilityOptions {}
    // merge our new feature's instance APIs with the existing table instance APIs
    interface Table<TData extends RowData> extends FilterAvailabilityInstance {}

    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        column?: ColumnMetaBase;
        filter?: {
            search?: {
                placeholder?: string;
                debounce?: number;
                type?: ComponentProps<typeof DebouncedSearch>['type'];
            };
            button?: FilterButtonProps;
        };
    }
}

/* eslint-enable @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unused-vars */

export const useTable: typeof useReactTable = options =>
    useReactTable({ _features: [...(options._features ?? []), FilterAvailabilityFeature], ...options });

const pageSizes = [10, 25, 50, 100];

export const Table = <DataType,>({
    table,
    columnDefs,
    pageSizeOptions = pageSizes,
    onRefreshClick,
    onExportClick,
    onResetClick,
    isLoading,
    tableAppearance = 'table',
    showGlobalSearch = false,
    globalSearchOptions,
    headerOptions = {
        showRefreshButton: true,
        showExportButton: true,
        showResetButton: false,
        filterPosition: 'left',
    },
    footerOptions = {
        displayed: true,
        hidePagination: false,
    },
    placeholder,
    belowHeaderContent,
    onRowClick,
    isRowDisabled,
    useImplicitRowSelection = false,
}: TableType<DataType>) => {
    useEffect(() => {
        table.setColumnVisibility(
            columnDefs.reduce(
                (accumulator, currentValue) => ({
                    ...accumulator,
                    [currentValue.id ?? '']: !currentValue.meta?.column?.isInvisible,
                }),
                {}
            )
        );
    }, []);

    const filters = [
        ...columnDefs
            .map((columnDef, index) => {
                const { filter } = columnDef.meta ?? {};
                const column = table.getColumn(columnDef.id ?? '');
                const isFilterAvailable = table.getFilterAvailability()[column?.id ?? ''] ?? true;
                return filter && column ? (
                    <Box key={index}>
                        <Filter
                            key={columnDef.id}
                            column={column}
                            filter={filter}
                            isDisabled={isLoading || !isFilterAvailable}
                        />
                    </Box>
                ) : null;
            })
            .filter(maybeFilter => maybeFilter !== null),
    ];
    const globalSearch = showGlobalSearch ? (
        <Box
            className={
                showGlobalSearch && globalSearchOptions?.fullWidth ? tableStyles.fullWidthFilter : undefined
            }
        >
            <DebouncedSearch
                onChange={value => table.setGlobalFilter(value)}
                placeholder={globalSearchOptions?.placeholder}
                value={table.getState().globalFilter}
                disabled={globalSearchOptions?.isDisabled}
                debounce={globalSearchOptions?.debounce}
                type={'search'}
                key={'global-search'}
            />
        </Box>
    ) : null;

    const rows = table.getRowModel().rows;

    /**
     * While introducing increase customization options for the footer, we want to persist the
     * existing render behavior for the Footer component to prevent regressions within existing UX
     * which utilize the Table component.
     *
     * For consumers which do not require the footer, we provide an escape hatch to hide the footer,
     * `footerOptions.displayed`. By default, it is `true`, thus we maintain the existing behavior
     * for consumers without requiring any changes.
     */
    const displayFooter =
        footerOptions.displayed === true || (footerOptions?.hidePagination && table.getPageCount() < 2);
    const innerRootClasses = useMemo(() => {
        const classes = [tableStyles.innerRoot];

        if (displayFooter) {
            classes.push(tableStyles.withFooter);
        }

        return classes.join(' ');
    }, [displayFooter]);

    return (
        <Box className={tableStyles.root}>
            <Box className={innerRootClasses}>
                {(headerOptions?.showRefreshButton ||
                    headerOptions.showExportButton ||
                    headerOptions?.showResetButton ||
                    belowHeaderContent ||
                    filters.length > 0) && (
                    <Stack gap={'0.25rem'} align={'flex-start'}>
                        <Group align={'center'} justify={'space-between'} w={'100%'}>
                            <Group gap={'16px'} style={{ flex: 1 }}>
                                {globalSearch}
                                {headerOptions.filterPosition !== 'right' && filters.length > 0 && filters}
                            </Group>
                            <Group gap={'8px'}>
                                {headerOptions.filterPosition === 'right' && filters.length > 0 && filters}
                                {headerOptions.showResetButton && (
                                    <Button
                                        size={'sm'}
                                        intent={'neutral'}
                                        appearance={'ghost'}
                                        onClick={onResetClick}
                                        disabled={isLoading}
                                    >
                                        Reset
                                    </Button>
                                )}
                                {headerOptions.showRefreshButton && (
                                    <ActionIcon
                                        size={'lg'}
                                        intent={'neutral'}
                                        appearance={'outline'}
                                        onClick={onRefreshClick}
                                        disabled={isLoading}
                                        aria-label="Refresh table"
                                    >
                                        <IconReload color={'#8A8985'} />
                                    </ActionIcon>
                                )}
                                {headerOptions.showExportButton && (
                                    <ActionIcon
                                        size={'lg'}
                                        intent={'neutral'}
                                        appearance={'outline'}
                                        disabled={isLoading}
                                        onClick={() => onExportClick?.()}
                                        aria-label="Export table"
                                    >
                                        <IconDownload color={'#8A8985'} />
                                    </ActionIcon>
                                )}
                            </Group>
                        </Group>
                        {belowHeaderContent}
                    </Stack>
                )}

                <table className={tableStyles.table}>
                    {tableAppearance === 'table' && (
                        <Box
                            component="thead"
                            className={headerPosition({ position: headerOptions.position })}
                        >
                            {table.getHeaderGroups().map(headerGroup => (
                                <HeaderRow key={headerGroup.id}>
                                    {headerGroup.headers.map(header => {
                                        return (
                                            <Header
                                                sortState={header.column.getIsSorted()}
                                                key={header.id}
                                                onClick={
                                                    header.column.columnDef.meta?.column?.isSortable
                                                        ? header.column.getToggleSortingHandler()
                                                        : undefined
                                                }
                                                isSortable={header.column.columnDef.meta?.column?.isSortable}
                                                isReversed={
                                                    header.column.columnDef.meta?.column?.isRightAligned
                                                }
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </Header>
                                        );
                                    })}
                                </HeaderRow>
                            ))}
                        </Box>
                    )}

                    <Box component={'tbody'}>
                        {rows.length > 0 &&
                            rows.map(row => {
                                const rowDisabled = isRowDisabled?.(row.original) ?? false;
                                return (
                                    <Row
                                        key={row.id}
                                        className={joinClassNames(
                                            onRowClick || useImplicitRowSelection
                                                ? tableStyles.clickableRow
                                                : undefined,
                                            tableStyles.row
                                        )}
                                        onClick={event => {
                                            if (useImplicitRowSelection) {
                                                const selectedHandler = row.getToggleSelectedHandler();
                                                selectedHandler(event);
                                            }
                                            onRowClick?.(row.index);
                                        }}
                                        disableBorders={tableAppearance === 'ghost'}
                                        isDisabled={rowDisabled}
                                    >
                                        {row.getVisibleCells().map(cell => {
                                            return (
                                                <Data
                                                    isReversed={
                                                        cell.column.columnDef.meta?.column?.isRightAligned
                                                    }
                                                    width={cell.column.columnDef.meta?.column?.width}
                                                    isDisabled={rowDisabled}
                                                    key={cell.id}
                                                >
                                                    {flexRender(
                                                        cell.column.columnDef.cell,
                                                        cell.getContext()
                                                    )}
                                                </Data>
                                            );
                                        })}
                                    </Row>
                                );
                            })}
                    </Box>
                </table>

                {rows.length === 0 && typeof placeholder !== 'undefined' && (
                    <TablePlaceholder placeholder={placeholder} />
                )}
            </Box>

            <LoadingOverlay visible={isLoading ?? false} />

            {displayFooter && (
                <Footer
                    table={table}
                    isDisabled={isLoading || rows.length === 0}
                    pageSizeOptions={pageSizeOptions}
                    footerAppearance={tableAppearance === 'table' ? 'normal' : 'minimal'}
                    position={footerOptions?.position}
                />
            )}
        </Box>
    );
};
